import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CharacterCounter } from '@/components/ui/character-counter';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GOAL_LABELS, NICHE_LABELS, TEMPLATE_GOALS, TEMPLATE_LIMITS, TEMPLATE_NICHES } from '@/lib/template-labels';
import { toastIfSilent } from '@/lib/template-toast';
import { formTemplatesService, type TemplateGoal, type TemplateNiche } from '@/services/formTemplates';
import toastService from '@/services/toast/service';

interface NewTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewTemplateDialog({ open, onOpenChange }: NewTemplateDialogProps) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [niche, setNiche] = useState<TemplateNiche | ''>('');
  const [goal, setGoal] = useState<TemplateGoal | ''>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setNiche('');
      setGoal('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || name.trim() === '' || niche === '' || goal === '') return;
    setSaving(true);
    try {
      const { publicId } = await formTemplatesService.create({
        name: name.trim(),
        description: description.trim(),
        niche,
        goal,
      });
      toastService.success('Template created');
      onOpenChange(false);
      navigate(`/templates/${publicId}`);
    } catch (err) {
      // A server message was already toasted by the api client; the dialog stays open with what was typed.
      toastIfSilent(err, "Couldn't create the template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New template</DialogTitle>
            <DialogDescription>
              Starts a draft form in the Template Studio with one question. Customers see nothing until you publish a
              version.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="new-template-name">Name</Label>
                <CharacterCounter current={name.length} max={TEMPLATE_LIMITS.name} />
              </div>
              <Input
                id="new-template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={TEMPLATE_LIMITS.name}
                placeholder="Roofing quote request"
                required
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                What customers see on the card. Name the result, e.g. "Roofing quote request".
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="new-template-description">Description</Label>
                <CharacterCounter current={description.length} max={TEMPLATE_LIMITS.description} />
              </div>
              <Input
                id="new-template-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={TEMPLATE_LIMITS.description}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">One line on what the form does.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-template-niche">Niche</Label>
                <Select value={niche} onValueChange={(v) => setNiche(v as TemplateNiche)} disabled={saving}>
                  <SelectTrigger id="new-template-niche" className="w-full">
                    <SelectValue placeholder="Select a niche" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_NICHES.map((n) => (
                      <SelectItem key={n} value={n}>
                        {NICHE_LABELS[n]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-template-goal">Goal</Label>
                <Select value={goal} onValueChange={(v) => setGoal(v as TemplateGoal)} disabled={saving}>
                  <SelectTrigger id="new-template-goal" className="w-full">
                    <SelectValue placeholder="Select a goal" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_GOALS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {GOAL_LABELS[g]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || name.trim() === '' || niche === '' || goal === ''}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create template'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
